/**
 * Central route definitions for the CRM and Admin surfaces.
 *
 * Every URL in the CRM and Admin lives here. Call sites import from this
 * module instead of constructing strings inline.
 *
 * Conventions:
 *   - Static routes are plain strings.
 *   - Routes with path params are typed functions that return a string.
 *   - Required query params are part of the function signature.
 *   - Optional query params come in an `opts` object as the last argument.
 *
 * Portal routes are excluded — they use a separate slug-prefix convention.
 * Marketing routes are excluded — they are not navigated to from app code.
 */

import { CONTACT_CARD_PARAM, CONTACT_RAIL_PARAM, type ContactCardId } from "@/lib/crm/contact-cards";
import type { LifecycleRowKey } from "@/lib/local-ui/local-ui-settings.schema";
import type { Urgency } from "@convex/lib/contactUrgency";
import type { BillingState } from "@convex/tables/customers";

/**
 * The ONE contact detail surface. Leads (prospects) and clients are the same
 * record type at different lifecycle positions, so they share a single route:
 * `/crm/contacts/[id]`. The nav folds per `recordType` inside the surface.
 * The legacy `/crm/clients/[id]` and `/crm/leads/[id]` routes are retired.
 *
 * The page is ONE shell (ruled 2026-09-16): every tab is a section of the
 * detail page, so a section's href is the page plus its fragment. Each tab
 * shows its section alone (2026-10-23); the continuous view, every section
 * stacked under the Overview, is the developer setting Contact Mono View. A
 * query string, when a section takes one (`?round=` for Disputes, `?invoice=`
 * for Billing, `?doc=` for Documents), sits between the path and the fragment.
 * Results, Fluid and the dev-only Convex inspector stay routes of their own.
 */
export type ContactSectionKey = "overview" | "account" | "tradelines" | "insights" | "disputes" | "documents" | "communications" | "notes" | "billing" | "activity";

/**
 * A link into a contact's page (ruled 2026-10-28: a link lands on everything it is about): the section (its tab) and
 * the card on it, and the row of the rail's Overview card, each when the link names one, with the round, invoice or
 * document the section opens at. Every link into the page is built by `contactLink`, its one spelling as an address.
 */
export type ContactLink = {
    section?: ContactSectionKey;
    /** The card on the section the page lands on (CustomerContactMainView reads `?card=`). */
    card?: ContactCardId;
    /** The rail Overview card's row that opens (ClientLayoutWrapper reads `?rail=`). */
    rail?: LifecycleRowKey;
    round?: number;
    invoice?: string;
    doc?: string;
    /** The Disputes round launcher starts auto-prepare (DisputeRoundStepAgentLauncher reads `?prepare=1`). */
    prepare?: true;
};

/**
 * The query parameters each section of a contact's page reads (2026-10-28): a tab switch keeps the target section's own
 * and drops the rest, so a document the Documents viewer had open (`?doc=`) does not follow the reader to Credit. The
 * Credit tab's Rounds card and the Disputes workspace both read the round; the workspace also its launcher's and its
 * Select step's. `skeleton` (the development loading-state preview) belongs to every section. The `card` and `rail`
 * parameters are a link's landing, removed once it lands.
 */
export const CONTACT_SECTION_PARAMS: Record<ContactSectionKey, readonly string[]> = {
    overview: ["round"],
    account: [],
    tradelines: [],
    insights: [],
    disputes: ["round", "prepare", "auto", "q", "sort"],
    documents: ["doc"],
    communications: [],
    notes: [],
    billing: ["invoice"],
    activity: [],
};
const EVERY_SECTION_PARAMS: readonly string[] = ["skeleton"];

/** The query a contact's page keeps on showing `section`: the parameters of `search` that section reads. */
export function contactSectionSearch(section: ContactSectionKey, search: string): URLSearchParams {
    const params = new URLSearchParams(search);
    const kept = new Set([...CONTACT_SECTION_PARAMS[section], ...EVERY_SECTION_PARAMS]);
    for (const key of [...params.keys()]) {
        if (!kept.has(key)) {
            params.delete(key);
        }
    }
    return params;
}

/** The address of a link into a contact's page. With no section it opens the contact's landing section. */
function contactLink(id: string, link: ContactLink = {}): string {
    const params = new URLSearchParams();
    if (link.round !== undefined) {
        params.set("round", String(normalizeRoundForRoute(link.round)));
    }
    if (link.invoice !== undefined) {
        params.set("invoice", link.invoice);
    }
    if (link.doc !== undefined) {
        params.set("doc", link.doc);
    }
    if (link.prepare) {
        params.set("prepare", "1");
    }
    if (link.card !== undefined) {
        params.set(CONTACT_CARD_PARAM, link.card);
    }
    if (link.rail !== undefined) {
        params.set(CONTACT_RAIL_PARAM, link.rail);
    }
    const search = params.size > 0 ? `?${params.toString()}` : "";
    return `/crm/contacts/${id}${search}${link.section ? `#${link.section}` : ""}`;
}

const crmContactRoutes = {
    list: "/crm/contacts",
    /** The client book's Disputes view, filtered to one automation value (the Dispute Engine's counts link here). */
    automation: (value: "ready" | "blocked" | "not_enrolled") => `/crm/contacts?tab=clients&view=disputes&automation=${value}`,
    /** The client book filtered as the Dashboard's Client Queue card is — its urgency and billing filters — full page
     *  (contactsInitialState reads `urgency` and `billing`). */
    queue: ({ urgencies = [], billingStates = [] }: { urgencies?: readonly Urgency[]; billingStates?: readonly BillingState[] }) => {
        const params = new URLSearchParams({ tab: "clients" });
        if (urgencies.length > 0) {
            params.set("urgency", urgencies.join(","));
        }
        if (billingStates.length > 0) {
            params.set("billing", billingStates.join(","));
        }
        return `/crm/contacts?${params.toString()}`;
    },
    import: "/crm/contacts/import",

    detail: (id: string) => contactLink(id),
    link: contactLink,
    account: (id: string) => contactLink(id, { section: "account" }),
    billing: (id: string, opts?: { invoice?: string; }) => contactLink(id, { section: "billing", ...(opts?.invoice !== undefined ? { invoice: opts.invoice } : {}) }),
    disputeRounds: (id: string, round?: number) => contactLink(id, { section: "disputes", ...(round !== undefined ? { round } : {}) }),
    documents: (id: string) => contactLink(id, { section: "documents" }),
    tradelines: (id: string) => contactLink(id, { section: "tradelines" }),
    insights: (id: string) => contactLink(id, { section: "insights" }),
    messages: (id: string) => contactLink(id, { section: "communications" }),
    convex: (id: string) => `/crm/contacts/${id}/convex`,
};

export const routes = {
    crm: {
        root: "/crm",
        login: "/crm/login",
        signup: "/crm/signup",
        // Where a verification link lands. It turns the company details parked at
        // signup into the owner's org, then forwards to the dashboard. Session-gated
        // and idempotent, so a re-click or a mail-scanner prefetch is harmless.
        welcome: "/crm/welcome",
        // The one surface that asks a signed-in owner what their company is
        // called. It RENDERS rather than redirecting, which is what makes it a
        // safe destination for the member-less branch of requireAuth: a
        // redirecting destination there ping-pongs forever.
        welcomeCompany: "/crm/welcome/company",
        dashboard: "/crm/dashboard",
        planner: "/crm/planner",
        messages: "/crm/messages",
        invoices: "/crm/invoices",
        profile: "/crm/profile",

        // The single contact detail surface (leads + clients).
        contacts: crmContactRoutes,

        // MERGE-ORPHAN-FIX (2026-08-02): `chatkit: "/crm/fluid/chatkit"` removed — main-next
        // deleted that page route in a1728ac241 ("fluid popout"), the merge honors the deletion,
        // and the key had zero callers. `src/services/fluid-chatkit/server.ts` already dropped its
        // matching `route:crm-fluid-chatkit` nav entity for the same reason.
        fluid: {
            root: "/crm/fluid",
            playground: "/crm/fluid/playground",
            thread: (threadId: string) => `/crm/fluid?threadId=${encodeURIComponent(threadId)}`,
        },

        organization: "/crm/organization",
        team: "/crm/team",
        plan: "/crm/plan",
        automations: "/crm/automations",
        /** One automation rule's full-page editor (2026-10-29): a rule by its id, or "new" (with `?template=` a
         *  template's draft, with `?authoring=` a Fluid run's), or with `run` one of the rule's runs laid over its canvas.
         *  The CRM shell draws no chrome around it. */
        automationRule: (ruleId: string, opts?: { run?: string; }) => `/crm/automations/${encodeURIComponent(ruleId)}${opts?.run === undefined ? "" : `?run=${encodeURIComponent(opts.run)}`}`,
        compliance: "/crm/compliance",
        disputeEngine: "/crm/dispute-engine",
        /** One automation run's page: its clients (from the runs table, on the Dispute Engine and the scheduler). */
        disputeEngineRun: (runId: string) => `/crm/dispute-engine/runs/${encodeURIComponent(runId)}`,
        designStudio: "/crm/design-studio",


        analytics: { hub: "/crm/analytics", outcomes: "/crm/analytics/outcomes" },

        /** The Activity Center (2026-09-21): the route stands, the page is still to come. */
        activity: "/crm/activity",

        // The Design Studio (2026-10-11): the client-facing design — portal, signup, plans, agreements,
        // letters and reports — named versions and one publish. It was the portal editor, at
        // /crm/portal-editor (now a redirect).
        designStudioEditor: "/crm/design-studio/editor",

        // MERGE-ORPHAN-FIX (2026-08-02): `builderNew`, `builder(id)` and `connectors(id)` removed —
        // they emitted `/crm/workflows/builder/...`, whose two page routes main-next deleted in
        // 8d4c8f237f ("consolidate workflow builders into action layer; remove legacy UI patterns").
        // The merge honors that deletion; the three keys had zero callers repo-wide.
        orchestrator: "/crm/orchestrator",
        workflows: { list: "/crm/workflows", background: "/crm/workflows?tab=background", detail: (runId: string) => `/crm/workflows/${encodeURIComponent(runId)}` },
    },

    admin: { root: "/admin", login: "/admin/login", audit: "/admin/audit", archived: "/admin/archived", impersonate: "/admin/impersonate", incidents: "/admin/incidents", operations: "/admin/operations", tenants: "/admin/tenants", waitlist: "/admin/waitlist" },
} as const;

function normalizeRoundForRoute(round: number): number {
    return Math.max(1, Math.trunc(Number.isFinite(round) ? round : 1));
}

/**
 * Returns the CRM login URL with a typed error code query param.
 * Use instead of `routes.crm.login + '?error=...'` string concatenation.
 */
export function loginWithError(code: "session_expired" | "unauthorized" | "auth_failed" | "callback_error" | "wrong_account"): string {
    return `${routes.crm.login}?error=${code}`;
}

/** A route's dynamic segment as its page reads it: a link encodes the id (encodeURIComponent); one that does not decode
 *  is read as written. The page and the prewarm of its reads (page-reads) both decode through this. */
export function decodeRouteSegment(segment: string): string {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
}
